import React from 'react';
import DocumentTitle from 'react-document-title';
import { FormattedMessage } from 'react-intl';
import {
  Stack,
  Box,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { NavigationCards } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import useBaseTableProps from '../../hooks/useBaseTableProps';
import TableLayout from '../../components/TableLayout';
import messages from './messages';
import { useUnitCompliance } from './hooks';
import BuildingNavigationCard from './BuildingNavigationCard';
import InitializeApplicableFractionsAction from '../../components/InitializeApplicableFractionsAction';

/**
 * Unit Compliance component - displays unit compliance information
 * for affordable housing properties
 */
function UnitCompliance() {
  const baseTableProps = useBaseTableProps();
  const {
    navigationCards,
    propertyBuildings,
    selectedProperty,
    selectedBuildingIds,
    hasSelectedBuildings,
    handleSelectBuilding,
    ...tableProps
  } = useUnitCompliance({
    intl: baseTableProps.intl,
    renderCardContent: BuildingNavigationCard,
  });

  const showSelectPrompt = !hasSelectedBuildings;
  // Use totalCount (pre-filter) so the table stays visible when a filter
  // produces 0 results — the table will show its own "no results" state.
  const showEmptyState =
    hasSelectedBuildings &&
    !tableProps.isLoading &&
    tableProps.totalCount === 0;
  const showTable = hasSelectedBuildings && tableProps.totalCount > 0;

  return (
    <DocumentTitle title={baseTableProps.intl.formatMessage(messages.title)}>
      <Stack
        p={3}
        sx={{
          backgroundColor: 'background.paper',
        }}
      >
        {/* Header Section */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
        >
          <Stack>
            <Typography variant="titleLarge" gutterBottom>
              <FormattedMessage {...messages.title} />
            </Typography>
            <Typography variant="bodyMedium" color="text.secondary">
              <FormattedMessage {...messages.subtitle} />
            </Typography>
          </Stack>
          <InitializeApplicableFractionsAction
            selectedProperty={selectedProperty}
            buildings={propertyBuildings}
            intl={baseTableProps.intl}
          />
        </Stack>

        {/* Building Cards Section */}
        <Box sx={{ mb: 4, width: '100%', overflowX: 'auto' }}>
          <Box sx={{ width: 'max-content', display: 'flex' }}>
            <NavigationCards
              cards={navigationCards}
              onChange={handleSelectBuilding}
              value={selectedBuildingIds}
            />
          </Box>
        </Box>

        {/* No building selected prompt */}
        {showSelectPrompt && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="bodyMedium" color="text.secondary">
              <FormattedMessage {...messages.selectBuildingPrompt} />
            </Typography>
          </Box>
        )}

        {/* Buildings selected but no NAUR events */}
        {showEmptyState && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="bodyMedium" color="text.secondary">
              <FormattedMessage {...messages.noNaurEventsMessage} />
            </Typography>
          </Box>
        )}

        {/* NAUR Events Table — only when events exist */}
        {showTable && (
          <Box>
            <TableLayout name={tableProps.name} childrenElementsHeight={50 + 8}>
              {(tableHeight) => (
                <>
                  <TableHeader
                    title={<FormattedMessage {...messages.naurTableTitle} />}
                    count={tableProps.count}
                    totalCount={tableProps.totalCount}
                  />
                  <Box
                    sx={{ display: 'inline-block', width: 'fit-content' }}
                  ></Box>
                  <Table
                    {...baseTableProps}
                    {...tableProps}
                    height={tableHeight}
                  />
                </>
              )}
            </TableLayout>
          </Box>
        )}
      </Stack>
    </DocumentTitle>
  );
}

export default UnitCompliance;
